import { Box, Skeleton, Stack, Typography } from '@mui/material';
import type { CreditPoolWithUnits } from '@vy/api-definition';
import { formatDate } from '@vy/date';
import { formatPrice } from '@vy/services';
import { CREDIT_KINDS, CREDIT_SCHEDULES, Currency, DEFAULT_CURRENCY } from '@vy/types';
import {
  formatNumber,
  isEmptyString,
  isNotEmptyString,
  isNotNullOrUndefined,
  isNullOrUndefined,
} from '@vy/utils';
import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useSearchParams } from 'react-router-dom';
import { withGlobalSearchParams } from '../../../components/utility/GlobalSearchParamsBoundary';
import { withSuspenseBoundary } from '../../../components/utility/SuspenseBoundary';
import {
  useGetCustomerPortalCreditPools,
  useGetCustomerPortalDataWithPageParams,
  useGetCustomerPortalMonthlyUsageData,
} from '../../../hooks/requests/use-customer-portal';
import { useDialog } from '../../../hooks/use-dialog';
import { CustomerPortalNavBar } from '../CustomerPortalNavBar/CustomerPortalNavBar';
import { ExpiredLink } from '../ExpiredLink/ExpiredLink';
import { PlatformFooter } from '../PlatformFooter/PlatformFooter';
import { CustomerPortalCustomerDetails } from './customer-portal-components/CustomerPortalCustomerDetails/CustomerPortalCustomerDetails';
import { UsageBreakdownModal } from './customer-portal-components/CustomerPortalCustomerDetails/CustomerPortalCustomerDetailsModal/UsageBreakdownModal';
import { CustomerPortalInvoicesTable } from './customer-portal-components/CustomerPortalInvoicesTable/CustomerPortalInvoicesTable';
import { PortalStatCard } from './customer-portal-components/PortalStatCard/PortalStatCard.tsx';
import { VyTooltip } from '../../../components/new-design/VyTooltip';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

function buildCreditPoolTitle(creditPool: CreditPoolWithUnits, currency: Currency): string {
  if (creditPool.creditKind === CREDIT_KINDS.MONETARY) {
    return formatPrice(creditPool.balance, currency);
  }

  return `${formatNumber(creditPool.balance)} ${creditPool.units ?? 'units'}`;
}

function buildProjectedBalanceLabel(
  creditPool: CreditPoolWithUnits,
  currency: Currency,
): string | null {
  if (creditPool.projectedBalance >= creditPool.balance) {
    return null;
  }

  if (creditPool.creditKind === CREDIT_KINDS.MONETARY) {
    return formatPrice(creditPool.projectedBalance, currency);
  }

  return `${formatNumber(creditPool.projectedBalance)} ${creditPool.units ?? 'units'}`;
}

function buildCreditPoolSubtitle(creditPool: CreditPoolWithUnits): React.ReactNode {
  if (isNullOrUndefined(creditPool.closestEntryExpiryDate)) {
    return null;
  }

  const dateLabel =
    creditPool.initialGrantSchedule === CREDIT_SCHEDULES.ONE_TIME ? 'Expires on' : 'Resets';

  return (
    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
      {`${dateLabel} ${formatDate(creditPool.closestEntryExpiryDate)}`}
    </Typography>
  );
}

const CustomerPortalPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const { data, isError, isLoading, isFetching } = useGetCustomerPortalDataWithPageParams(token, {
    enabled: isNotEmptyString(token),
  });

  const { isDialogOpen, handleOpen, handleClose } = useDialog();

  const { data: monthlyUsageData } = useGetCustomerPortalMonthlyUsageData(
    { token },
    {
      suspense: false,
      enabled: isNotEmptyString(token) && isNotNullOrUndefined(data),
    },
  );

  const { data: creditPoolsData } = useGetCustomerPortalCreditPools(token, {
    enabled: isNotEmptyString(token) && isNotNullOrUndefined(data),
  });

  const creditPools = creditPoolsData?.creditPools ?? [];
  const [selectedCreditPool, setSelectedCreditPool] = useState<CreditPoolWithUnits | null>(null);

  const handleCreditPoolClick = (clickedCreditPool: CreditPoolWithUnits) => {
    setSelectedCreditPool(clickedCreditPool);
    handleOpen();
  };

  const handleModalClose = () => {
    setSelectedCreditPool(null);
    handleClose();
  };

  const hasUsageData = isNotNullOrUndefined(monthlyUsageData) && monthlyUsageData.length > 0;

  const currency = data?.currency ?? DEFAULT_CURRENCY;

  if (isEmptyString(token) || isError || (isNullOrUndefined(data) && !isLoading)) {
    return <ExpiredLink />;
  }

  return (
    <>
      <Helmet>
        <title>Vayu: Customer Portal</title>
      </Helmet>

      <div
        style={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <CustomerPortalNavBar
          logo={data?.account.logoUrl}
          isLoading={isLoading}
          customerName={data?.account.name}
        />

        <Box
          sx={{
            padding: '20px 122px',
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            overflowY: 'auto',
          }}
        >
          {isLoading || isNullOrUndefined(data) ? (
            <>
              <Box sx={{ mb: 3 }}>
                <Skeleton variant="rectangular" width="100%" height={120} />
              </Box>

              <Box>
                <Skeleton variant="rectangular" width="100%" height={50} sx={{ mb: 1 }} />
                <Skeleton variant="rectangular" width="100%" height={400} />
              </Box>
            </>
          ) : (
            <Stack sx={{ gap: 1 }}>
              <CustomerPortalCustomerDetails
                customer={data.customer}
                onUsageClick={hasUsageData ? handleOpen : undefined}
              />

              {creditPools.length > 0 && (
                <Stack direction="row" sx={{ gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  {creditPools.map((creditPool) => {
                    const projectedBalanceLabel = buildProjectedBalanceLabel(creditPool, currency);

                    return (
                      <Box key={creditPool.id} sx={{ flexGrow: 1 }}>
                        <PortalStatCard
                          label="Available credits"
                          title={buildCreditPoolTitle(creditPool, currency)}
                          subTitle={buildCreditPoolSubtitle(creditPool)}
                          icon={<AccountBalanceWalletOutlinedIcon sx={{ color: '#0521FF', fontSize: '18px' }} />}
                          titleAdornment={
                            isNotNullOrUndefined(projectedBalanceLabel) ? (
                              <VyTooltip
                                tooltipContent={`Projected balance after pending (Draft & In Review) invoices: ${projectedBalanceLabel}`}
                              >
                                <InfoOutlinedIcon
                                  sx={{ fontSize: 16, color: 'text.secondary', cursor: 'default' }}
                                />
                              </VyTooltip>
                            ) : undefined
                          }
                          onClick={() => handleCreditPoolClick(creditPool)}
                        />
                      </Box>
                    );
                  })}
                </Stack>
              )}

              <CustomerPortalInvoicesTable
                invoices={data.invoices}
                customer={data.customer}
                token={token}
                totalInvoicesCount={data.totalInvoicesCount}
                invoicesSummary={data.invoicesSummary}
                isFetching={isFetching}
              />

              {isNotNullOrUndefined(monthlyUsageData) && (
                <UsageBreakdownModal
                  open={isDialogOpen}
                  onClose={handleModalClose}
                  monthlyUsageData={monthlyUsageData}
                  commitmentSummaries={data.commitmentSummaries}
                  currency={data.currency}
                  creditPool={selectedCreditPool ?? (creditPools.length === 1 ? creditPools[0] : undefined)}
                />
              )}
            </Stack>
          )}
        </Box>
        <PlatformFooter />
      </div>
    </>
  );
};

const CustomerPortalPageWithSuspenseBoundary = withSuspenseBoundary(
  withGlobalSearchParams(CustomerPortalPage),
);

export { CustomerPortalPageWithSuspenseBoundary as CustomerPortalPage };
